import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AlertTriangle, TrendingUp } from "lucide-react";
import { pricingApi, CHANNEL_LABEL } from "@/services/pricing";
import { PageShell, Pill, LoadingState } from "@/shared/ui/PageHeader";
import { formatCents } from "@/services/finance";

export function PricingDashboardPage() {
  const dash = useQuery({ queryKey: ["pricing", "dashboard"], queryFn: pricingApi.dashboard });
  if (dash.isLoading || !dash.data) return <LoadingState />;
  const d = dash.data;

  return (
    <PageShell
      title="定价管理"
      subtitle="4 渠道差异化定价 · D3B 老板审批生效"
      actions={
        <Link
          to="/admin/pricing/change-requests"
          className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 text-[13px] font-medium"
        >
          变更日志 ({d.pending_changes_count} 待审)
        </Link>
      }
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        {d.channel_markups.map((m) => (
          <div key={m.channel} className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="text-[12px] font-medium text-slate-500">{CHANNEL_LABEL[m.channel]}</div>
            <div className="mt-2 text-[28px] font-bold text-slate-900">{m.markup_pct}%</div>
            <div className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-500">
              {m.tax_included && <Pill tone="muted">含税</Pill>}
              {m.fee_included && <Pill tone="muted">含费</Pill>}
              {m.pending_change_id && <Pill tone="warning">待审变更</Pill>}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <section className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-base font-semibold">
              <TrendingUp className="h-4 w-4 text-emerald-500" /> 利润 Top 5
            </h3>
          </div>
          <ul>
            {d.profit_top5.map((row) => (
              <li
                key={row.product}
                className="flex items-center justify-between border-b border-slate-100 py-2.5 last:border-0"
              >
                <div className="text-[13px] font-medium text-slate-900">{row.product}</div>
                <div className="flex items-center gap-3">
                  <span className="text-[12px] text-slate-500">{formatCents(row.gmv_cents)}</span>
                  <Pill tone="success">{row.profit_pct}%</Pill>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-base font-semibold">
              <AlertTriangle className="h-4 w-4 text-amber-500" /> 异常告警
            </h3>
          </div>
          {d.anomaly_alerts.length === 0 ? (
            <div className="text-[13px] text-slate-500">无异常</div>
          ) : (
            <ul>
              {d.anomaly_alerts.map((a, i) => (
                <li key={i} className="flex items-center justify-between border-b border-slate-100 py-2.5 last:border-0">
                  <div className="text-[13px] text-slate-900">{a.product}</div>
                  <Pill tone={a.severity === "warning" ? "warning" : "danger"}>{a.alert}</Pill>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-[13px] text-blue-700">
        共 <strong>{d.b2b_overrides_count}</strong> 条 B2B 专属价 · {" "}
        <Link to="/admin/pricing/overrides" className="font-semibold hover:underline">
          查看 →
        </Link>
      </div>
    </PageShell>
  );
}
